/**
 * ChatContext — Persists chat state across tab navigation and page refreshes.
 *
 * This provider wraps the CopilotShell so that when ChatPage unmounts
 * (e.g. user switches to Accounts tab) the messages array stays alive.
 * On page refresh, messages are restored from sessionStorage (keyed by
 * user ID for per-salesperson isolation).
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
} from "react";
import { useSSEChat, type UseSSEChatReturn } from "../hooks/useSSEChat";
import { useAuth } from "../hooks/useAuth";
import { useAccountContext } from "./AccountContext";
import { persistToSession, loadFromSession, clearSession } from "./chatSessionStorage";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ChatContext = createContext<UseSSEChatReturn | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { selectedAccountUuid } = useAccountContext();

  // Restore session data for this user (if available) before hook initialises
  const sessionData = useMemo(() => {
    if (!userId) return null;
    return loadFromSession(userId);
  }, [userId]);

  // The shared SSE chat instance — lives for the entire shell lifecycle
  const chat = useSSEChat({
    accountUuid: selectedAccountUuid ?? undefined,
    initialMessages: sessionData?.messages,
    initialConversationId: sessionData?.conversationId,
  });

  // ── Auto-load conversation from server if we have an ID but no messages ──
  // This handles: fresh page load with localStorage conversation_id but no
  // sessionStorage messages (e.g. sessionStorage was cleared, or first load
  // after login with "stay logged in").
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    const convId = chat.conversationId;
    if (convId && chat.messages.length === 0 && !chat.isLoadingHistory) {
      autoLoadedRef.current = true;
      chat.loadConversation(convId);
    }
  }, [chat.conversationId, chat.messages.length, chat.isLoadingHistory, chat.loadConversation]);

  // ── Persist state to sessionStorage on change (debounced 250ms) ──────
  const prevMessagesRef = useRef(chat.messages);
  const prevConvIdRef = useRef(chat.conversationId);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!userId) return;
    // Only schedule write if something actually changed
    if (
      chat.messages !== prevMessagesRef.current ||
      chat.conversationId !== prevConvIdRef.current
    ) {
      prevMessagesRef.current = chat.messages;
      prevConvIdRef.current = chat.conversationId;

      // Debounce to avoid serialization storms during rapid state updates
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
      persistTimerRef.current = setTimeout(() => {
        persistToSession(userId, chat.messages, chat.conversationId);
        persistTimerRef.current = null;
      }, 250);
    }

    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
    };
  }, [userId, chat.messages, chat.conversationId]);

  // ── Wrap clearConversation to also clear sessionStorage ──────────────
  const originalClear = chat.clearConversation;
  const wrappedClear = useMemo(() => {
    return () => {
      originalClear();
      if (userId) {
        clearSession(userId);
      }
    };
  }, [originalClear, userId]);

  const value = useMemo<UseSSEChatReturn>(
    () => ({
      ...chat,
      clearConversation: wrappedClear,
    }),
    [chat, wrappedClear],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChatContext(): UseSSEChatReturn {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChatContext must be used within <ChatProvider>");
  }
  return ctx;
}
